import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { Button } from "@/components/primitives/Button";
import { libraryClips, fmtDuration, type LibraryClip } from "@/lib/montageApi";

interface Props {
  onPick: (clipId: string) => Promise<void>;
  onClose: () => void;
}

export function ClipPicker({ onPick, onClose }: Props) {
  const { t } = useTranslation();
  const [clips, setClips] = useState<LibraryClip[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    libraryClips()
      .then(setClips)
      .catch(() => {
        toast.error("Impossibile caricare i clip");
        setClips([]);
      });
  }, []);

  const pick = async (id: string) => {
    setBusy(id);
    try {
      await onPick(id);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={onClose}>
      <div
        data-testid="clip-picker"
        onClick={(e) => e.stopPropagation()}
        className="glass w-full max-w-lg rounded-2xl p-6"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-base font-semibold uppercase tracking-wide">I tuoi clip pronti</h2>
          <button data-testid="clip-picker-close" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        {clips === null ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : clips.length === 0 ? (
          <p data-testid="clip-picker-empty" className="py-8 text-center text-sm text-muted-foreground">
            Nessun clip READY nei tuoi progetti.
          </p>
        ) : (
          <ul className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
            {clips.map((c) => (
              <li
                key={c.id}
                data-testid={`clip-picker-item-${c.id}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-[#2E7BFF]/20 bg-[#05070D]/60 p-3 transition-colors hover:border-[#2E7BFF]/50"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{c.project_title || "Progetto"} · {c.scene_title || "Clip"}</p>
                  <p className="text-xs text-muted-foreground">{fmtDuration(c.duration)}</p>
                </div>
                <Button
                  data-testid={`clip-picker-add-${c.id}`}
                  size="sm" variant="secondary" disabled={busy !== null}
                  onClick={() => pick(c.id)}
                >
                  {busy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                  Aggiungi
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
